import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { HashRouter as Router, Route } from 'react-router-dom';
import { i18nIntl } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import ActionLink from '../builder/ActionLink';
import ActionButton, { BackButton, MainButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection.js';
import { DeleteButton } from '../builder/ConfirmButton';
import FloatingLayer from '../builder/FloatingLayer';
import Model from '../builder/Model';
import SaveButton from '../builder/SaveButton';
import SaveResponseMessage from '../builder/SaveResponseMessage';
import SearchField from '../builder/SearchField';
import SectionSubtitle from '../builder/SectionSubtitle';
import SectionTitle from '../builder/SectionTitle';
import Select from '../builder/Select';
import Spinner from '../builder/Spinner';

import ItemTypeIndicator from '../sweeps/ItemTypeIndicator';

import Module from '../ui_items/Module.js';
import SectionHeader from '../ui_items/SectionHeader.js';
import OwnerPromoWrapper from '../ui_items/OwnerPromoWrapper.js';
import RibbonCollection from '../ui_items/RibbonCollection.js';

const CustomTemplatesIndex = (props) => {
	useCSS('comp/templates.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ in_progress, setInProgress ] = useState(false);
	const [ just_finished_creating, setJustFinishedCreating ] = useState(false);
	const [ filters, setFilters ] = useState({ brand:'----', type:'----', search:"" });
	const [ templates, setTemplates ] = useState(props.templates);
	const [ select_label, setSelectLabel ] = useState(false);
	const [ create_model, setCreateModel ] = useState({ current_brand:false });

	const is_agent_admin = window?.is_agent_admin ?? false;
	const organizers = props?.organizers ?? [];
	const only_contents = props?.only_contents ?? false;
	const from = props?.from ?? false;
	const editor = props?.editor ?? false;

	const types = Object.values(props?.types ?? {});

	const selectOrganizing = (template_id) => {
		setSelectLabel(template_id);
	};

	const changeCreateModel = (k, v) => {
		setCreateModel({ ...create_model, [k]:v });
	};

	const useTemplate = (id, cb) => {
		setInProgress(true);

		const template = templates.find((t) => (t.CorporateTemplate.id === id));

		if(!template) return false;

		let promo_profile_domain = '';
		switch(template.CorporateTemplate.platform) {
			case '0':
				promo_profile_domain = window.basic_base_url;
				break;

			case '1':
				promo_profile_domain = window.premium_base_url;
				break;

			case '2':
			default:
				promo_profile_domain = window.wl_base_url;
				break;
		}

		$.ajaxSetup({
			crossDomain:true,
			xhrFields:{
				withCredentials:true,
			},
		});

		const data = {
			template_id:id,
			organizer_id:create_model.current_brand,
		};

		$.post(`${promo_profile_domain}agents/create_from_template`, data, (res) => {
			setInProgress(false);
			setJustFinishedCreating(res);

			if(cb != undefined) cb();
		}, 'JSON');
	};

	const goToPromoProfile = () => {
		if(just_finished_creating.status != '1') return;

		window.location.href = just_finished_creating.cb_url;
	};

	const simplifyTextToSearch = (text) => {
		return text.toLowerCase().replace(/s/gi, "");
	};

	const match_tag = (template) => {
		const labels = template.TemplateLabel;
		const type = template.Promotion.promotion_type_id;

		const text_to_search = simplifyTextToSearch(`${template.CorporateTemplate.title}__${template.CorporateTemplate.short_description}`);

		let match = false;
		if(filters.brand == "----" && filters.type == "----" && filters.search == "") return true;

		if(is_agent_admin && filters.brand != "----") {
			labels.map((item) => {
				if(item?.Label?.id == filters.brand) {
					match = true;
				}
			});
		}

		if(filters.type != "----" && filters.type == type) match = true;

		if(filters.search != "") {
			const _search = simplifyTextToSearch(filters.search);
			if(text_to_search.indexOf(_search) > -1) match = true;
		}

		return match;
	};

	const changeFilter = (k, v) => {
		setFilters({ ...filters, [k]:v });
	};

	const toggleDetailLayer = () => {
		setSelectLabel(false);
		changeCreateModel('current_brand', false);

		switch(props.from) {
			case 'sidebar':
				window.location.href = '/promotions#';
				break;

			default:
				window.location.href = '/agents/templates#';
				break;
		}
	};

	const deleteCustomTemplate = (id) => {
		if(id == undefined) return false;

		const selected_template = templates.find((t) => {
			return obj.CorporateTemplate.id === id;
		});

		if(selected_template) {
			$.post('/agents/delete_template', { template_id:id }, (res) => {
				if(res.status == '1') {
					window.location.hash = '#';
					
					setTimeout(() => {
						const _templates = [...templates];
						setTemplates(_templates.splice([templates.indexOf(selected_template)]))
					}, 20);
				}
			}, 'JSON');
		}
	};

	const openTemplate = (template_id) => {
		window.location.hash = `view/${template_id}`;
	};

	const organizer_values = [{ k:'----', v:`-- ${i18n("All")} --`}].concat(organizers.map((o) => ({ k:o.id, v:o.name })));

	const getBrandOpitons = () => {
		const values = [{ k:'----', v:`-- ${i18n("All")} --` }];

		templates.map((template) => {
			const tags = Object.values(template.TemplateLabel);

			tags.map((item) => {
				if(item.Label == undefined) return;

				const already_in_array = (values.find((t) => (t.k == item.Label.id)) != undefined) ? true : false;
				if(already_in_array) return;

				values.push({ k:item.Label.id, v:item.Label.name });
			});
		});

		return values;
	};

	const getTypeOpitons = () => {
		const values = [{ k:'----', v:`-- ${i18n("***All")} --` }];

		types.map((type) => {
			const already_in_array = (values.find((t) => (t.k == type.id)) != undefined) ? true : false;
			if(already_in_array) return;

			values.push({ k:type.id, v:type.name });
		});

		return values;
	}

	const filtered_templates = [];
	templates.map((template) => {
		if(match_tag(template)) {
			filtered_templates.push(template);
		}
	});

	const page_title = (editor === false) ? i18n("Pick one of the following templates to create a new promotion from it") : <FormattedMessage id="Pick one of the following templates to create a new promotion from it or {link}"
	values={{link:<ActionLink _href={`${window.panel_url}templates`}>view Easypromos' templates</ActionLink>}} />;

	if(is_agent_admin && templates.length == 0) {
		return (
			<OwnerPromoWrapper
				menu={false}
				add_module={false} 
				custom_header={<SectionHeader
					title={i18n("You haven't created any templates yet.")}
					icon={{ icon:'knk-page' }}
				/>}
			>
				<Module force_show={true}>
					<p>
						<FormattedMessage
							id="Go to the promotion you want to create a template from, and click on {button_label} button to start."
							values={{
								button_label:i18n("Convert to template"),
							}}
						/>
					</p>
				</Module>
			</OwnerPromoWrapper>
		)
	}

	if(templates.length == 0) {
		return (
			<OwnerPromoWrapper
				menu={false}
				add_module={false} 
				custom_header={<SectionHeader
					title={i18n("You don't have any templates available yet.")}
					icon={{ icon:'knk-page' }}
				/>}
			>
				<Module force_show={true}>
					<p>{i18n("Please contact your administrator.")}</p>
				</Module>
			</OwnerPromoWrapper>
		)
	}

	const brand_values = getBrandOpitons();
	const type_values = getTypeOpitons();

	return (
		<Router hashType="noslash">
			<OwnerPromoWrapper
				menu={false}
				add_module={false} 
				sticky_header='-15px'
				custom_header={(!only_contents) ? <SectionHeader
					title={page_title}
					icon={{ icon:'knk-page' }}
					filters={<div>
						<Model
							classname="filter_section gui_equal_columns gui_padded_children_low gui_flex_align_bottom"
							model={filters}
							change={changeFilter}
						>
							{(is_agent_admin) && (
								<Select
									_class={{ first:true }}
									name="brand"
									values={brand_values}
								>Organizing brand</Select>
							)}

							<Select
								_class={{ first:true }}
								name="type"
								values={type_values}
							>Promotion type</Select>

							<SearchField
								_class={{ first:true }}
								name="search"
								placeholder="Search by name"
							>Search</SearchField>
						</Model>
					</div>}
				/> : null}
			>
				<Module>
					{(filtered_templates.length > 0) ? (
						<ul class="gui_templates_list gui_not_in_bottom_tiny">
							{filtered_templates.map((item, i) => {
								const template = item.CorporateTemplate;
								const promotion = item.Promotion;
								const tags = Object.values(item.TemplateLabel);

								const bg_image = (template.image != undefined && template.image != "") ? template.full_image : '/img/custom_templates_default_bgs/custom_template_' + ((window.is_spanish) ? 'spa' : 'eng') + '.jpg';

								const template_styles = {
									backgroundColor:(template.bg_color != "") ? template.bg_color : 'rgba(128, 128, 128, 1)',
									backgroundImage:'url(' + bg_image + ')',
								};

								const ribbons = [];
								tags.map((label, l) => {
									if(label.Label == undefined) return null;

									ribbons.push({
										title:label.Label.name,
									});
								});

								return (
									<li key={i} class={'gui_template' + ((in_progress) ? ' gui_remove_pointer_events' : '')}>
										<div class="gui_template_image" style={template_styles}>
											<img src={`${window.cdn_url}img/blank_120x63.png`} width="100%" alt="" />

											<div class="element_hover">
												<ButtonCollection>
													<ActionButton>More information</ActionButton>
												</ButtonCollection>
											</div>
										</div>

										<div class="template_info">
											<RibbonCollection
												items={ribbons}
											/>

											<ButtonCollection _class={{other:'gui_not_margined_v'}}>
												<ItemTypeIndicator
													type={promotion.promo_type}
												/>
											</ButtonCollection>

											<span class="template_title">
												<strong class="gui_not_in_end_small">{template.title}</strong>
											</span>

											<p class="short_description gui_not_in_top_tiny gui_left_aligned gui_small_text gui_hidden_overflowed">{template.short_description.replace(/\n/g, ' ')}</p>

											{(in_progress) && (
												<Spinner
													force_bg={true}
													fullscreen={true}
												>Creating promotion from template</Spinner>
											)}
										</div>

										<ActionLink
											onClick={() => openTemplate(template.id)}
											_class={{ other:'template_click' }}
										/>
									</li>
								)
							})}
						</ul>
					) : (
						<p>{i18n("No templates were found with this filter.")}</p>
					)}
				</Module>

				<Route exact={true} path="/view/:template_id" render={(route) => {
					let selected_template = false;
					if(route.match.params.template_id != undefined) {
						selected_template = templates.find((t) => {
							return t.CorporateTemplate.id === route.match.params.template_id;
						});
					}
					if(!selected_template) toggleDetailLayer();

					const template = selected_template.CorporateTemplate;
					const promotion = selected_template.Promotion;

					var promo_profile_domain = '';
					switch(template.platform) {
						case '0':
							promo_profile_domain = window.basic_base_url;
							break;

						case '1':
							promo_profile_domain = window.premium_base_url;
							break;

						case '2':
						default:
							promo_profile_domain = window.wl_base_url;
							break;
					}

					return (
						<FloatingLayer backButton={() => toggleDetailLayer()} _class={{size:'small', other:'gui_single_template_layer'}}>
							{(!select_label) && (
								<div>
									<SectionTitle autotranslate={false}>{template.title}</SectionTitle>

									<p class="short_description gui_left_aligned gui_overflowed_auto" dangerouslySetInnerHTML={{ __html:template.short_description.replace(/\n/g, '<br />') }} />

									{(template.instructions != "") && (
										<div class="template_instructions gui_postit gui_not_in_top">
											<p><strong>{i18n("Instructions")}:</strong></p>
											<div dangerouslySetInnerHTML={{ __html:template.instructions.replace(/\n/g, '<br />') }} />
										</div>
									)}

									<ButtonCollection in_floating={true}>
										<MainButton
											onClick={() => selectOrganizing(template.id)}
										>Use template</MainButton>

										{(is_agent_admin) && (
											<>
												<ActionButton
													_href={`${promo_profile_domain}promotions/profile/${promotion.id}`}
													_target='_blank'
												>Edit template</ActionButton>

												{(from === false || from != 'sidebar') && (
													<DeleteButton
														onClick={() => deleteCustomTemplate(template.id)}
													>Delete template</DeleteButton>
												)}
											</>
										)}
									</ButtonCollection>
								</div>
							)}

							{(select_label) && (
								<div>
									<SectionTitle>Select your Organizing brand</SectionTitle>

									<Model model={create_model} change={changeCreateModel}>
										<Select
											name="current_brand"
											values={organizer_values}
											_class={{ first:true, size:50 }}
										>Organizing brand</Select>
									</Model>

									<ButtonCollection in_floating={true}>
										<SaveButton
											disabled={(create_model.current_brand === false) ? true : false}
											onClick={(e, cb) => useTemplate(template.id, cb)}
										>Use template</SaveButton>

										<BackButton
											onClick={() => setSelectLabel(false)}
										/>
									</ButtonCollection>
								</div>
							)}
						</FloatingLayer>
					)
				}} />

				{(just_finished_creating) && (
					<SaveResponseMessage
						status={(just_finished_creating.status == '1') ? 'copied' : 'ko'}
						message={just_finished_creating.msg}
						onClose={() => goToPromoProfile()}
					/>
				)}
			</OwnerPromoWrapper>
		</Router>
	)
};

export default injectIntl(CustomTemplatesIndex);